// 📄 components/credenciales/CredentialsPanel.tsx
// Vista principal del vault de credenciales del proyecto — header con
// estadísticas + botón de alta, filtros, grid de cards, formulario inline
// de creación/edición y modal de confirmación de borrado.
import React, { useMemo, useState } from 'react';
import { KeyRound, Plus, Users, Lock } from 'lucide-react';
import { useAuth } from '../../context/AuthContext.tsx';
import { useCredentials } from '../../hooks/useCredentials.ts';
import { CredentialAmbiente, CredentialInput, CredentialMetadata } from '../../services/credentialService.ts';
import CredentialCard from './CredentialCard.tsx';
import CredentialForm from './CredentialForm.tsx';
import CredentialDeleteModal from './CredentialDeleteModal.tsx';
import { FlujoAprobacionModal } from './FlujoAprobacionCredencial.tsx';

interface CredentialsPanelProps {
  projectId: string;
}

// `credentials` ya viene filtrado por firestore.rules a "propias +
// compartidas" (ver credentialService.ts) — este filtro solo subdivide
// ESE conjunto para la UI, no es una capa de seguridad adicional.
type FiltroTipo = 'todas' | 'mias' | 'compartidas-conmigo';
const AMBIENTES: CredentialAmbiente[] = ['DEV', 'QAS', 'PRD', 'OTRO'];

export default function CredentialsPanel({ projectId }: CredentialsPanelProps) {
  const { user } = useAuth();
  const { credentials, loading, error, createCredential, updateCredential, deleteCredential } = useCredentials(projectId);

  const [formOpen, setFormOpen] = useState(false);
  const [editando, setEditando] = useState<CredentialMetadata | null>(null);
  const [eliminando, setEliminando] = useState<CredentialMetadata | null>(null);
  const [aprobacion, setAprobacion] = useState<CredentialMetadata | null>(null);
  const [filtroAmbiente, setFiltroAmbiente] = useState<CredentialAmbiente | 'todos'>('todos');
  const [filtroTipo, setFiltroTipo] = useState<FiltroTipo>('todas');

  const stats = useMemo(() => {
    const mias = credentials.filter((c) => c.creadoPor === user?.uid).length;
    const compartidasConmigo = credentials.filter((c) => c.compartida && c.creadoPor !== user?.uid).length;
    return { total: credentials.length, mias, compartidasConmigo };
  }, [credentials, user?.uid]);

  const visibles = useMemo(() => {
    return credentials.filter((c) => {
      if (filtroAmbiente !== 'todos' && c.ambiente !== filtroAmbiente) return false;
      if (filtroTipo === 'mias' && c.creadoPor !== user?.uid) return false;
      if (filtroTipo === 'compartidas-conmigo' && !(c.compartida && c.creadoPor !== user?.uid)) return false;
      return true;
    });
  }, [credentials, filtroAmbiente, filtroTipo, user?.uid]);

  const abrirCreacion = () => { setEditando(null); setFormOpen(true); };
  const abrirEdicion = (cred: CredentialMetadata) => { setEditando(cred); setFormOpen(true); };
  const cerrarForm = () => { setFormOpen(false); setEditando(null); };

  const handleSave = async (data: CredentialInput) => {
    if (editando) {
      await updateCredential(editando.id, data);
    } else {
      await createCredential(data);
    }
    cerrarForm();
  };

  const handleDelete = async () => {
    if (!eliminando) return;
    await deleteCredential(eliminando.id);
    setEliminando(null);
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-blue-400" /> Credenciales del proyecto
          </h3>
          <p className="text-xs text-gray-500 mt-1 flex items-center gap-3">
            <span>{stats.total} credenciales</span>
            <span className="flex items-center gap-1"><Lock className="w-3 h-3" /> {stats.mias} mías</span>
            <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {stats.compartidasConmigo} compartidas conmigo</span>
          </p>
        </div>
        {!formOpen && (
          <button
            type="button"
            onClick={abrirCreacion}
            className="flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 px-4 py-2 rounded-lg text-sm font-semibold transition-all flex-shrink-0"
          >
            <Plus className="w-4 h-4" /> Nueva credencial
          </button>
        )}
      </div>

      {formOpen && (
        <CredentialForm credential={editando || undefined} onSave={handleSave} onCancel={cerrarForm} />
      )}

      {credentials.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <select
            value={filtroAmbiente}
            onChange={(e) => setFiltroAmbiente(e.target.value as CredentialAmbiente | 'todos')}
            className="bg-zinc-800 border border-white/10 rounded-lg px-2.5 py-1.5"
          >
            <option value="todos">Todos los ambientes</option>
            {AMBIENTES.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <select
            value={filtroTipo}
            onChange={(e) => setFiltroTipo(e.target.value as FiltroTipo)}
            className="bg-zinc-800 border border-white/10 rounded-lg px-2.5 py-1.5"
          >
            <option value="todas">Todas</option>
            <option value="mias">Mis credenciales</option>
            <option value="compartidas-conmigo">Compartidas conmigo</option>
          </select>
        </div>
      )}

      {loading && (
        <div className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(280px,1fr))] animate-pulse">
          <div className="h-48 bg-white/5 border border-white/10 rounded-2xl" />
          <div className="h-48 bg-white/5 border border-white/10 rounded-2xl" />
          <div className="h-48 bg-white/5 border border-white/10 rounded-2xl" />
        </div>
      )}

      {!loading && error && (
        <p className="text-sm text-gray-500 text-center py-6">{error}</p>
      )}

      {!loading && !error && credentials.length === 0 && (
        <div className="text-center py-12 bg-white/5 border border-white/10 rounded-2xl text-gray-500">
          <KeyRound className="w-8 h-8 mx-auto mb-2 text-gray-600" />
          <p className="text-sm">Todavía no hay credenciales guardadas en este proyecto.</p>
          <p className="text-xs mt-1">Crea la primera para guardar accesos de proveedores, APIs o sistemas de forma segura.</p>
        </div>
      )}

      {!loading && !error && credentials.length > 0 && visibles.length === 0 && (
        <p className="text-sm text-gray-500 text-center py-6">Ninguna credencial coincide con los filtros.</p>
      )}

      {!loading && !error && visibles.length > 0 && (
        <div className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(280px,1fr))]">
          {visibles.map((cred) => (
            <CredentialCard
              key={cred.id}
              credential={cred}
              isOwner={cred.creadoPor === user?.uid}
              onEdit={() => abrirEdicion(cred)}
              onDelete={() => setEliminando(cred)}
              onContrasena={() => setAprobacion(cred)}
            />
          ))}
        </div>
      )}

      {aprobacion && (
        <FlujoAprobacionModal
          credencial={{ tipo: 'vault', projectId, credId: aprobacion.id }}
          titulo={aprobacion.nombre}
          onClose={() => setAprobacion(null)}
        />
      )}

      {eliminando && (
        <CredentialDeleteModal
          nombre={eliminando.nombre}
          onConfirm={handleDelete}
          onCancel={() => setEliminando(null)}
        />
      )}
    </div>
  );
}
